import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Avatar,
  Collapse,
  IconButton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import MicroTargets from "./MicroTargets";
import { Box } from "@mui/system";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import "../WebsiteTarget.css";
import { useSelector } from "react-redux";
import { setPage } from "../../../../../features/websiteTargetSlice";
import store from "../../../../../store";
import { formatDate, formatPercentage } from "../../../../../utils/helpers";

interface Props {
  tableHeadings: { [key: string]: string };
  websiteTarget: any;
  selectedSharedUid: any;
  formattedWebsiteTarget: any;
  websiteMicroTarget: any;
  siteOptions: any;
  hasEditWebsiteTargetPermission: any;
}

const MicroTableHeadings = {
  geo: "Geo",
  product: "Product",
  spend: "Spend",
  clicks: "Clicks",
  ftd: "FTD",
  cvr: "CVR",
  cac: "CAC",
  profit: "Profit",
  nrpu: "NRPU",
  cpa: "CPA",
  rs: "RS",
  total_income: "Total Income",
  cpa_pv: "CPA PV",
  rs_pv: "RS PV",
  pv: "PV",
  last_modified: "Last Modified",
  modified_by: "Modified By",
};

// helper function to check if the target is editable
const isTargetEditable = (targetMonth: string): boolean => {
  if (!targetMonth) return false;
  const [month, year] = targetMonth.split("/").map(Number);
  const quarterStartMonth = Math.floor((month - 1) / 3) * 3 + 1;
  const deadline = new Date(year, quarterStartMonth - 1, 15);
  const today = new Date();
  return today <= deadline;
};

const WebsiteTargetTable = ({
  formattedWebsiteTarget,
  websiteMicroTarget,
  hasEditWebsiteTargetPermission,
}: Props) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const page = useSelector((state: any) => state.websiteTarget.page);
  const [selectedTargetSharedUid, setSelectedTargetSharedUid] = useState(
    null
  ) as any;
  const selectedPeriod = useSelector(
    (state: any) => state.websiteTarget.selectedPeriod
  );
  const selectedDataSet = useSelector(
    (state: any) => state.websiteTarget.selectedDataSet
  );
  const [openRow, setOpenRow] = React.useState<number | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleChangePage = (_: unknown, newPage: number) => {
    store.dispatch(setPage(newPage));
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    store.dispatch(setPage(0));
  };

  const handleClickMoreInfo = (row: any) => {
    setOpenRow(openRow === row?.shared_uid ? null : row?.shared_uid);
    setSelectedTargetSharedUid(row?.shared_uid);
  };

  const [sort, setSort] = useState<{
    keytoSort: string | undefined;
    direction: "asc" | "desc" | undefined;
  }>({
    keytoSort: "month",
    direction: "desc",
  });

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    setSort({
      keytoSort: header.KEY,
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const filteredWebsiteTarget = formattedWebsiteTarget?.filter(
    (target: any) => {
      const statusTargetPeriod =
        selectedPeriod && selectedPeriod.length !== 0
          ? selectedPeriod.includes(target.month)
          : true;
      return statusTargetPeriod;
    }
  );

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    store.dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
  }, [searchParams]);

  useEffect(() => {
    if (page !== 0) {
      store.dispatch(setPage(0));
    }
    // eslint-disable-next-line
  }, [selectedPeriod && page]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;
        if (keytoSort === undefined) {
          return 0;
        }
        let aValue, bValue;

        if (keytoSort === "month") {
          // Parse the month and year for sorting
          const [monthA, yearA] = a[keytoSort].split("/").map(Number);
          const [monthB, yearB] = b[keytoSort].split("/").map(Number);

          // Create comparable values: year first, then month
          aValue = yearA * 100 + monthA; // e.g., 202301 for January 2023
          bValue = yearB * 100 + monthB; // e.g., 202301 for January 2023
        } else {
          aValue = a[keytoSort];
          bValue = b[keytoSort];
        }

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  const sortedRows = getSortedData(filteredWebsiteTarget || []);
  const paginatedTarget = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const columns: {
    [key: string]: {
      id: number;
      KEY: string;
      label: string;
      sortable: boolean;
    }[];
  } = {
    "Key Metrics": [
      { id: 0, KEY: "month", label: "Period", sortable: true },
      { id: 1, KEY: "spend", label: "Spend", sortable: true },
      { id: 2, KEY: "clicks", label: "Clicks", sortable: true },
      { id: 3, KEY: "ftd", label: "FTD", sortable: true },
      { id: 4, KEY: "cvr", label: "CVR", sortable: true },
      { id: 5, KEY: "cac", label: "CAC", sortable: true },
      { id: 6, KEY: "profit", label: "Profit", sortable: true },
      { id: 7, KEY: "nrpu", label: "NRPU", sortable: true },
      { id: 8, KEY: "modified", label: "Last Modified", sortable: true },
      { id: 9, KEY: "modified_by", label: "Modified By", sortable: true },
    ],
    Income: [
      { id: 0, KEY: "month", label: "Period", sortable: true },
      { id: 1, KEY: "cpa", label: "CPA", sortable: true },
      { id: 2, KEY: "rs", label: "RS", sortable: true },
      { id: 3, KEY: "total_income", label: "Total Income", sortable: true },
      { id: 4, KEY: "cpa_pv", label: "CPA PV", sortable: true },
      { id: 5, KEY: "rs_pv", label: "RS PV", sortable: true },
      { id: 6, KEY: "pv", label: "PV", sortable: true },
      { id: 7, KEY: "net_revenue", label: "Net Revenue", sortable: true },
      { id: 8, KEY: "avg_rs_deal_p", label: "Avg RS Deal", sortable: true },
      { id: 9, KEY: "epc", label: "EPC", sortable: true },
      { id: 10, KEY: "epc_cpa", label: "EPC CPA", sortable: true },
      { id: 11, KEY: "epc_rs", label: "EPC RS", sortable: true },
    ],
    Performance: [
      { id: 0, KEY: "month", label: "Period", sortable: true },
      { id: 1, KEY: "growth_target", label: "Growth Target", sortable: true },
      { id: 2, KEY: "active_users", label: "Active Users", sortable: true },
      {
        id: 3,
        KEY: "old_active_users",
        label: "Old Active Users",
        sortable: true,
      },
      {
        id: 4,
        KEY: "acquired_active_users",
        label: "Acquired Active Users",
        sortable: true,
      },
      { id: 5, KEY: "churn_rate", label: "Churn Rate", sortable: true },
      {
        id: 6,
        KEY: "ratio_between_acquired_to_ftd",
        label: "Acquired To FTD",
        sortable: true,
      },
      {
        id: 7,
        KEY: "avg_net_rev_per_active_user",
        label: "Avg Net Rev To Active Users",
        sortable: true,
      },
      {
        id: 8,
        KEY: "br_to_ng_active_rs_ratio",
        label: "BR to Ng Active RS",
        sortable: true,
      },
    ],
    All: [
      { id: 0, KEY: "month", label: "Period", sortable: true },
      { id: 1, KEY: "spend", label: "Spend", sortable: true },
      { id: 2, KEY: "clicks", label: "Clicks", sortable: true },
      { id: 3, KEY: "ftd", label: "FTD", sortable: true },
      { id: 4, KEY: "cvr", label: "CVR", sortable: true },
      { id: 5, KEY: "cac", label: "CAC", sortable: true },
      { id: 6, KEY: "profit", label: "Profit", sortable: true },
      { id: 7, KEY: "nrpu", label: "NRPU", sortable: true },
      { id: 10, KEY: "cpa", label: "CPA", sortable: true },
      { id: 11, KEY: "rs", label: "RS", sortable: true },
      { id: 12, KEY: "total_income", label: "Total Income", sortable: true },
      { id: 13, KEY: "cpa_pv", label: "CPA PV", sortable: true },
      { id: 14, KEY: "rs_pv", label: "RS PV", sortable: true },
      { id: 15, KEY: "pv", label: "PV", sortable: true },
      { id: 16, KEY: "net_revenue", label: "Net Revenue", sortable: true },
      { id: 17, KEY: "avg_rs_deal_p", label: "Avg RS Deal", sortable: true },
      { id: 18, KEY: "epc", label: "EPC", sortable: true },
      { id: 19, KEY: "epc_cpa", label: "EPC CPA", sortable: true },
      { id: 20, KEY: "epc_rs", label: "EPC RS", sortable: true },
      { id: 21, KEY: "growth_target", label: "Growth Target", sortable: true },
      { id: 22, KEY: "active_users", label: "Active Users", sortable: true },
      {
        id: 23,
        KEY: "old_active_users",
        label: "Old Active Users",
        sortable: true,
      },
      {
        id: 24,
        KEY: "acquired_active_users",
        label: "Acquired Active Users",
        sortable: true,
      },
      { id: 25, KEY: "churn_rate", label: "Churn Rate", sortable: true },
      {
        id: 26,
        KEY: "ratio_between_acquired_to_ftd",
        label: "Acquired To FTD",
        sortable: true,
      },
      {
        id: 27,
        KEY: "avg_net_rev_per_active_user",
        label: "Avg Net Rev To Active Users",
        sortable: true,
      },
      {
        id: 28,
        KEY: "br_to_ng_active_rs_ratio",
        label: "BR to Ng Active RS",
        sortable: true,
      },
      { id: 29, KEY: "modified", label: "Last Modified", sortable: true },
      { id: 30, KEY: "modified_by", label: "Modified By", sortable: true },
    ],
  };

  const handleRowClick = (row: any) => {
    const params = new URLSearchParams(location.search);
    params.set("websiteTargetSharedUid", row?.shared_uid);
    params.set("page", "0"); // Reset page to 0
    navigate(`edit_projection?${params.toString()}`);
  };

  // Get the columns based on the selected data set
  const selectedColumns = columns[selectedDataSet] || [];

  // Updated formatting functions
  const formatNumber = (num: number | string | undefined) => {
    if (num === undefined) return "N/A";
    const numValue = typeof num === "string" ? parseFloat(num) : num;
    if (isNaN(numValue)) return "N/A";
    return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const formatCurrency = (num: number | string | undefined) => {
    if (num === undefined) return "N/A";
    const numValue = typeof num === "string" ? parseFloat(num) : num;
    if (isNaN(numValue)) return "N/A";
    return `${numValue?.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}$`;
  };

  // const formatPercentage = (num: number | string | undefined) => {
  //   if (num === undefined) return "N/A";
  //   const numValue = typeof num === "string" ? parseFloat(num) : num;
  //   if (isNaN(numValue)) return "N/A";
  //   const rounded = Math.round(numValue * 20) / 20;
  //   return `${rounded.toFixed(2)}%`;
  // };

  return (
    <>
      <TableContainer
        className="monthlyTargetListewrap"
        component={Paper}
        sx={{
          padding: "0px 0px",
          boxShadow: "none",
          borderStartStartRadius: "0px",
        }}
      >
        <Table
          aria-label="collapsible table"
          size="small"
          className={selectedDataSet}
          stickyHeader
        >
          <TableHead>
            <TableRow>
              {selectedColumns.map((header) => (
                <TableCell key={header.id}>
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell key="more_info_header" sx={{ textAlign: "center" }}>
                More Info
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTarget.length > 0 ? (
              paginatedTarget?.map((row: any) => (
                <React.Fragment key={row.cron_id}>
                  <TableRow>
                    <TableCell align="left">
                      <Box
                        sx={{
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {hasEditWebsiteTargetPermission ? (
                          isTargetEditable(row?.month) ? (
                            <IconButton
                              onClick={() => handleRowClick(row)}
                              data-tracker="93"
                              size="small"
                              style={{ marginRight: "5px" }}
                            >
                              <CreateOutlinedIcon
                                fontSize="small"
                                color="secondary"
                              />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              style={{ marginRight: "5px" }}
                              disabled
                              title="Editing period has expired"
                            >
                              <CreateOutlinedIcon fontSize="small" />
                            </IconButton>
                          )
                        ) : (
                          <IconButton
                            size="small"
                            disabled
                            style={{ marginRight: "5px" }}
                            title="No edit permission"
                            color="secondary"
                          >
                            <CreateOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                        {row?.month ? row.month : "N/A"}
                      </Box>
                    </TableCell>
                    {selectedColumns.map((column) => {
                      if (column.KEY === "modified_by") {
                        return (
                          <TableCell key={column.KEY} align="left">
                            <Stack
                              flexDirection={"row"}
                              alignItems={"center"}
                              gap={1}
                            >
                              <Avatar
                                alt={
                                  row?.modified_by ? row?.modified_by : "N/A"
                                }
                                src="#"
                                sx={{ width: 24, height: 24 }}
                              />
                              {row?.modified_by ? row?.modified_by : "N/A"}
                            </Stack>
                          </TableCell>
                        );
                      } else if (column.KEY === "modified") {
                        return (
                          <TableCell key={column.KEY} align="left">
                            {row?.modified ? formatDate(row?.modified) : "N/A"}
                          </TableCell>
                        );
                      } else if (
                        column.KEY === "br_to_ng_active_rs_ratio" ||
                        column.KEY === "cvr" ||
                        column.KEY === "growth_target" ||
                        column.KEY === "ratio_between_acquired_to_ftd" ||
                        column.KEY === "avg_rs_deal_p" ||
                        column.KEY === "churn_rate"
                      ) {
                        return (
                          <TableCell key={column.KEY} align="left">
                            {row[column.KEY] !== undefined ||
                            row[column.KEY] !== null
                              ? formatPercentage(row[column.KEY])
                              : 0}
                          </TableCell>
                        );
                      }

                      // else if (
                      //   column.KEY === "growth_target" ||
                      //   column.KEY === "ratio_between_acquired_to_ftd" ||
                      //   column.KEY === "avg_rs_deal_p" ||
                      //   column.KEY === "churn_rate"
                      // ) {
                      //   return (
                      //     <TableCell key={column.KEY} align="left">
                      //       {row[column.KEY] !== undefined
                      //         ? formatPercentage(row[column.KEY])
                      //         : "N/A"}
                      //     </TableCell>
                      //   );
                      // }
                      else if (column.KEY === "avg_net_rev_per_active_user") {
                        return (
                          <TableCell key={column.KEY} align="left">
                            {row[column.KEY] !== undefined
                              ? formatCurrency(row[column.KEY])
                              : "N/A"}
                          </TableCell>
                        );
                      } else if (
                        column.KEY === "clicks" ||
                        column.KEY === "ftd" ||
                        column.KEY === "active_users" ||
                        column.KEY === "old_active_users" ||
                        column.KEY === "acquired_active_users"
                      ) {
                        return (
                          <TableCell key={column.KEY} align="left">
                            {row[column.KEY] !== undefined
                              ? formatNumber(row[column.KEY])
                              : "N/A"}
                          </TableCell>
                        );
                      } else if (column.KEY === "nrpu") {
                        return (
                          <TableCell key={column.KEY} align="left">
                            {row[column.KEY] !== null ? row[column.KEY] : 0}
                          </TableCell>
                        );
                      } else if (
                        column.KEY === "spend" ||
                        column.KEY === "profit" ||
                        column.KEY === "cac" ||
                        column.KEY === "cpa" ||
                        column.KEY === "rs" ||
                        column.KEY === "total_income" ||
                        column.KEY === "cpa_pv" ||
                        column.KEY === "rs_pv" ||
                        column.KEY === "pv" ||
                        column.KEY === "net_revenue" ||
                        column.KEY === "epc" ||
                        column.KEY === "epc_cpa" ||
                        column.KEY === "epc_rs"
                      ) {
                        return (
                          <TableCell key={column.KEY} align="left">
                            {row[column.KEY] !== undefined
                              ? formatCurrency(row[column?.KEY])
                              : "N/A"}
                          </TableCell>
                        );
                      } else if (column.KEY !== "month") {
                        return (
                          <TableCell key={column.KEY} align="left">
                            {row[column.KEY] !== undefined
                              ? row[column.KEY]
                              : "N/A"}
                          </TableCell>
                        );
                      }
                      return null;
                    })}
                    <TableCell>
                      <Box
                        className="stickyhighlight"
                        sx={{ textAlign: "center" }}
                      >
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          color="secondary"
                          onClick={() => handleClickMoreInfo(row)}
                        >
                          {openRow === row?.shared_uid ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      style={{ padding: 0, border: "none" }}
                      colSpan={selectedColumns.length + 1}
                    >
                      <Collapse
                        in={openRow === row?.shared_uid}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ padding: "5px 0px" }}>
                          <MicroTargets
                            MicroTableHeadings={MicroTableHeadings}
                            selectedTargetSharedUid={selectedTargetSharedUid}
                            websiteMicroTarget={websiteMicroTarget}
                            selectedDataSet={selectedDataSet}
                            formatDate={formatDate}
                          />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={selectedColumns.length + 2} // +2 for Edit + More Info columns
                  align="center"
                  sx={{
                    padding: "20px",
                    color: "text.secondary",
                  }}
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(filteredWebsiteTarget?.length / rowsPerPage)}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={page + 1}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
      </TableContainer>
    </>
  );
};

export default WebsiteTargetTable;
